/**
 * Custom hook for order edit step configuration
 */

import {useMemo} from 'react'

import {STEP_TYPE_TO_EDIT_TYPE} from './bulkOrderEditConfig'
import {useFieldConfigurations, useStepConfigurations} from './hooks/useRegionConfig'
import {EditableFieldType} from './RmcOrderEditStepView'

/**
 * Step configuration object for order edit view
 */
export type OrderEditStepConfig = {
  editStepType: EditableFieldType
}

/**
 * Hook to get dynamic edit step configurations based on region features
 * This leverages the same step configuration logic used in the stepper
 */
export const useOrderEditableFieldConfigurations = (): OrderEditStepConfig[] => {
  // Get the stepper step configurations
  const stepConfigs = useStepConfigurations()

  const fieldConfigs = useFieldConfigurations()

  const editableFieldConfigs = useMemo(() => {
    return fieldConfigs
      .filter((fieldConfig) => fieldConfig.isEnabled)
      .map((fieldConfig) => ({
        editStepType: fieldConfig.name as EditableFieldType
      }))
  }, [fieldConfigs])

  // Transform stepper step configs to edit step configs
  const editStepConfigs = useMemo(() => {
    // Filter out steps that are disabled or don't have an edit type mapping
    const editableSteps = stepConfigs
      .filter((config) => config.isEnabled && STEP_TYPE_TO_EDIT_TYPE[config.name] !== null)
      .map((config) => ({
        editStepType: STEP_TYPE_TO_EDIT_TYPE[config.name] as EditableFieldType
      }))

    return editableSteps
  }, [stepConfigs])

  return [...editStepConfigs, ...editableFieldConfigs]
}
